import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Shield,
  Building2,
  MapPin,
  ArrowRight,
  ChevronRight,
  Zap,
  Clock,
  BarChart3,
  CheckCircle2,
  LogOut,
  UserCheck,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { getUserProviderManagers, getUserCompanyManagers, getUserProviderResources } from '../../services/staffingService';
import { logout } from '../../services/userService';
import { useUser } from '../../context/UserContext';
import logoImg from '../_asset/logo/logo.svg';

// ─── Animation helpers ────────────────────────────────────────────────────────
const containerVariants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.07, delayChildren: 0.1 } },
};
const itemVariants = {
  hidden: { opacity: 0, y: 14 },
  show: { opacity: 1, y: 0, transition: { duration: 0.4, ease: [0.22, 1, 0.36, 1] } },
};

// ─── Role config ──────────────────────────────────────────────────────────────
const ROLE = {
  agency: {
    accent: '#404880',
    accentSoft: 'rgba(64, 72, 128, 0.10)',
    label: 'Agency Portal',
    icon: Shield,
    gradient: 'linear-gradient(150deg, #3a4178 0%, #404880 45%, #5b63a8 100%)',
    headline: 'Run your off-duty program\nlike clockwork.',
    blurb: 'Dispatch shifts, approve rates, and keep every detail on track — all in one command center.',
    features: [
      { icon: Zap, text: 'Dispatch shifts to your officers in seconds' },
      { icon: Clock, text: 'Track coverage and approvals in real time' },
      { icon: BarChart3, text: 'Automated invoicing and reporting' },
    ],
    stat: 'Trusted across 20,000+ law enforcement agencies',
  },
  customer: {
    accent: '#CC457D',
    accentSoft: 'rgba(204, 69, 125, 0.10)',
    label: 'Customer Portal',
    icon: Building2,
    gradient: 'linear-gradient(150deg, #b83a6e 0%, #CC457D 45%, #e173a0 100%)',
    headline: 'Sworn officers, on-site\nwhen you need them.',
    blurb: 'Post a work order and reach hundreds of thousands of officers nationwide in minutes.',
    features: [
      { icon: Zap, text: 'Create a work order in minutes' },
      { icon: Shield, text: 'Reach 700k+ sworn officers nationwide' },
      { icon: CheckCircle2, text: 'Approve rates and track coverage live' },
    ],
    stat: '20,000+ agencies · 700k+ officers',
  },
  'customer-on-agency': {
    accent: '#404880',
    accentSoft: 'rgba(64, 72, 128, 0.10)',
    label: 'Customer Portal',
    icon: Building2,
    gradient: 'linear-gradient(150deg, #3a4178 0%, #404880 45%, #5b63a8 100%)',
    headline: 'Sworn officers, on-site\nwhen you need them.',
    blurb: 'Post a work order and reach hundreds of thousands of officers nationwide in minutes.',
    features: [
      { icon: Zap, text: 'Create a work order in minutes' },
      { icon: Shield, text: 'Reach 700k+ sworn officers nationwide' },
      { icon: CheckCircle2, text: 'Approve rates and track coverage live' },
    ],
    stat: '20,000+ agencies · 700k+ officers',
  },
  officer: {
    accent: '#1f7a5c',
    accentSoft: 'rgba(31, 122, 92, 0.10)',
    label: 'Officer Portal',
    icon: UserCheck,
    gradient: 'linear-gradient(150deg, #1b6b50 0%, #1f7a5c 45%, #3fa984 100%)',
    headline: 'Take up off-duty shifts\non your schedule.',
    blurb: 'View and accept work orders from agencies near you — right from your phone.',
    features: [
      { icon: Zap, text: 'See available shifts near you' },
      { icon: CheckCircle2, text: 'Accept work orders in a tap' },
      { icon: Clock, text: 'Manage your schedule on the go' },
    ],
    stat: 'Trusted by 700k+ sworn officers',
  },
};

// ─── Minimal panel with top-left logo ────────────────────────────────────────
const MinimalCenteredPanel = ({ variant }) => {
  const cfg = ROLE[variant];
  const RoleIcon = cfg.icon;
  return (
    <div
      className="relative hidden overflow-hidden lg:flex lg:w-[35%] lg:flex-col lg:justify-between lg:px-8 lg:py-10"
      style={{ background: cfg.gradient }}
    >
      {/* decorative blobs - subtle background */}
      <div className="pointer-events-none absolute -right-32 -top-32 h-80 w-80 rounded-full bg-white/5 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-40 -left-24 h-96 w-96 rounded-full bg-black/10 blur-3xl" />

      {/* top-left logo */}
      <div className="logo flex items-center gap-2 relative z-10">
        <img src={logoImg} alt="logo" className="w-auto h-8 md:h-10 object-contain" />
        <span className="text-lg font-bold text-white">Officer.app</span>
      </div>

      {/* center icon accent */}
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6, ease: 'easeOut', delay: 0.2 }}
        className="absolute inset-0 flex items-center justify-center pointer-events-none"
      >
        <div className="flex h-24 w-24 items-center justify-center rounded-full bg-white/10 backdrop-blur-sm">
          <RoleIcon size={40} className="text-white/40" />
        </div>
      </motion.div>

      {/* bottom: trust stat */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: 'easeOut', delay: 0.3 }}
        className="relative z-10 flex items-center gap-2 text-xs font-medium text-white/70"
      >
        <CheckCircle2 size={13} />
        {cfg.stat}
      </motion.div>
    </div>
  );
};

// ─── Company card (light) ─────────────────────────────────────────────────────
const CompanyCard = ({ variant, to, name, location, badge }) => {
  const cfg = ROLE[variant];
  return (
    <motion.div variants={itemVariants}>
      <Link
        to={to}
        className="group flex items-center justify-between rounded-xl border border-gray-200 bg-white px-4 py-3.5 transition-all hover:-translate-y-0.5 hover:border-gray-300 hover:shadow-lg hover:shadow-gray-200/60 no-underline"
      >
        <div className="flex items-center gap-3">
          <div
            className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl text-sm font-bold"
            style={{ backgroundColor: cfg.accentSoft, color: cfg.accent }}
          >
            {badge}
          </div>
          <div>
            <p className="text-sm font-semibold text-gray-900 leading-tight">{name}</p>
            {location && (
              <p className="mt-0.5 flex items-center gap-1 text-xs text-gray-400">
                <MapPin size={11} />
                {location}
              </p>
            )}
          </div>
        </div>
        <span
          className="flex h-7 w-7 items-center justify-center rounded-full transition-all group-hover:translate-x-0.5"
          style={{ color: cfg.accent }}
        >
          <ChevronRight size={18} />
        </span>
      </Link>
    </motion.div>
  );
};

// ─── Content column (light) ───────────────────────────────────────────────────
const ContentColumn = ({ variant, companies, footer, userName, userEmail, onLogout }) => {
  const cfg = ROLE[variant];
  const RoleIcon = cfg.icon;
  const initial = userName ? userName.charAt(0).toUpperCase() : '?';

  return (
    <div className="flex flex-1 flex-col px-6 py-10 sm:px-10 lg:px-14 lg:py-16">
      {/* top bar: mobile logo left, logout right */}
      <div className="mb-10 flex items-center justify-between">
        <div className="logo flex items-center gap-2 lg:hidden">
          <img src={logoImg} alt="logo" className="w-auto h-8 md:h-10 object-contain" />
          <span className="text-xl sm:text-2xl font-bold text-[#404880]">Officer.app</span>
        </div>
        {onLogout && (
          <div className="ml-auto">
            <LogoutButton onClick={onLogout} />
          </div>
        )}
      </div>

      <div className="flex flex-1 flex-col justify-center">
        <div className="mx-auto w-full max-w-md">

          {/* User greeting row */}
          <div className="mb-8 flex items-center gap-3">
            <div
              className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full text-sm font-bold text-white shadow-sm"
              style={{ background: cfg.gradient }}
            >
              {initial}
            </div>
            <div>
              <p className="text-base font-semibold text-gray-900 leading-tight">
                {userName ? `Hi, ${userName}` : 'Welcome back'}
              </p>
              {userEmail && (
                <p className="mt-0.5 text-xs text-gray-400">{userEmail}</p>
              )}
            </div>
          </div>

          {/* Role label + divider */}
          <div className="mb-4 flex items-center gap-3">
            <div className="flex items-center gap-1.5" style={{ color: cfg.accent }}>
              <RoleIcon size={13} />
              <span className="text-xs font-semibold uppercase tracking-widest">{cfg.label}</span>
            </div>
            <div className="flex-1 h-px bg-gray-100" />
          </div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="show"
            className="space-y-2.5"
          >
            {companies}
          </motion.div>

          {footer && <div className="mt-7">{footer}</div>}
        </div>
      </div>
    </div>
  );
};

// ─── Ghost footer link ────────────────────────────────────────────────────────
const GhostLink = ({ href, children, external }) => (
  <a
    href={href}
    {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
    className="group inline-flex items-center gap-1 text-xs font-medium text-gray-400 transition-colors hover:text-gray-700 no-underline"
  >
    {children}
    <ArrowRight size={12} className="transition-transform group-hover:translate-x-0.5" />
  </a>
);

const LogoutButton = ({ onClick, className = '' }) => (
  <button
    onClick={onClick}
    className={`inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-50 hover:text-gray-900 ${className}`}
  >
    <LogOut size={13} />
    Log out
  </button>
);

// ─── Builders for card lists ──────────────────────────────────────────────────
const buildAgencyCards = (companies) =>
  companies.map((c, i) => {
    const id = c.provider_company_id?._id;
    const name = c.provider_company_id?.agency_name || 'Agency';
    const location =
      [
        c.provider_company_id?.agency_county_name,
        c.provider_company_id?.agency_state_abbreviation,
      ]
        .filter(Boolean)
        .join(', ') || null;
    return (
      <CompanyCard
        key={id || i}
        variant="agency"
        to={`/provider-company/${id}/dashboard`}
        name={name}
        location={location}
        badge={<Shield size={18} />}
      />
    );
  });

const buildCustomerCards = (companies) =>
  companies.map((c, i) => {
    const id = c.customer_company_id?._id;
    const name = c.customer_company_id?.companyLegalName || 'Company';
    const location = c.customer_company_id?.formatted_location || null;
    return (
      <CompanyCard
        key={id || i}
        variant="customer"
        to={`/customer-company/${id}`}
        name={name}
        location={location}
        badge={name.charAt(0).toUpperCase()}
      />
    );
  });

const buildOfficerCards = (resources) =>
  resources.map((c, i) => {
    const id = c.provider_company_id?._id;
    const name = c.provider_company_id?.agency_name || 'Agency';
    const location =
      [
        c.provider_company_id?.agency_county_name,
        c.provider_company_id?.agency_state_abbreviation,
      ]
        .filter(Boolean)
        .join(', ') || null;
    return (
      <CompanyCard
        key={id || i}
        variant="officer"
        to={`/provider-resource/${id}/mobile`}
        name={name}
        location={location}
        badge={<UserCheck size={18} />}
      />
    );
  });

// ─── Portal section (grid tile) ───────────────────────────────────────
const PortalSection = ({ variant, cards, footer }) => {
  const cfg = ROLE[variant];
  const Icon = cfg.icon;
  return (
    <section className="flex flex-col rounded-2xl border border-gray-200 bg-white p-6 sm:p-7">
      <div
        className="mb-5 flex items-center gap-2 rounded-xl px-3 py-2.5"
        style={{ background: cfg.gradient }}
      >
        <Icon size={15} className="text-white" />
        <span className="text-xs font-semibold uppercase tracking-widest text-white">
          {cfg.label}
        </span>
      </div>
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="show"
        className="flex-1 space-y-2.5"
      >
        {cards}
      </motion.div>
      {footer && <div className="mt-6 border-t border-gray-100 pt-4">{footer}</div>}
    </section>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────
const MainView = () => {
  const { userInfo } = useUser();
  const [providerCompanies, setProviderCompanies] = useState([]);
  const [customerCompanies, setCustomerCompanies] = useState([]);
  const [providerResources, setProviderResources] = useState([]);
  const [loading, setLoading]                     = useState(true);
  
  const userName  = userInfo?.firstName || null;
  const userEmail  = userInfo?.email     || null;

  const handleLogout = async () => {
    try {
      const resp = await logout();
      window.location.href = resp?.data?.logoutUrl || '/';
    } catch (error) {
      toast.error('Could not log out. Please try again.');
    }
  };

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [r1, r2, r3] = await Promise.all([
          getUserProviderManagers(),
          getUserCompanyManagers(),
          getUserProviderResources(),
        ]);
        setProviderCompanies(r1.data || []);
        setCustomerCompanies(r2.data || []);
        setProviderResources(r3.data || []);
      } catch (err) {
        toast.error('Failed to load portal data');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  const hasAgency = providerCompanies.length > 0;
  const hasCustomer = customerCompanies.length > 0;
  const hasResource = providerResources.length > 0;

  // ── Loading ──
  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-200 border-t-[#404880]" />
      </div>
    );
  }

  // ── Build a portal section for each role this account has access to ──
  const sections = [];
  if (hasAgency) {
    sections.push(
      <PortalSection
        key="agency"
        variant="agency"
        cards={buildAgencyCards(providerCompanies)}
        footer={
          <GhostLink href="https://officer.app/request-officer" external>
            I want to request an officer
          </GhostLink>
        }
      />
    );
  }
  if (hasCustomer) {
    sections.push(
      <PortalSection
        key="customer"
        variant="customer"
        cards={buildCustomerCards(customerCompanies)}
        footer={<GhostLink href="mailto:hello@officer.app">Become a partner</GhostLink>}
      />
    );
  }
  if (hasResource) {
    sections.push(
      <PortalSection
        key="officer"
        variant="officer"
        cards={buildOfficerCards(providerResources)}
      />
    );
  }

  // ── No portal access (edge case) ──
  if (sections.length === 0) {
    return (
      <div className="flex min-h-screen flex-col bg-white font-inter">
        <header className="flex items-center justify-between px-6 py-5 sm:px-10">
          <div className="logo flex items-center gap-2">
            <img src={logoImg} alt="logo" className="w-auto h-8 md:h-10 object-contain" />
            <span className="text-xl sm:text-2xl font-bold text-[#404880]">Officer.app</span>
          </div>
          <LogoutButton onClick={handleLogout} />
        </header>
        <div className="flex flex-1 flex-col items-center justify-center gap-4">
          <p className="text-sm text-gray-400">No portal access found for this account.</p>
          <GhostLink href="mailto:hello@officer.app">Contact support</GhostLink>
        </div>
      </div>
    );
  }

  // ── Portal grid — one tile per role (agency / customer / officer) ──
  const initial = userName ? userName.charAt(0).toUpperCase() : '?';
  const gridCols =
    sections.length >= 3 ? 'lg:grid-cols-3' : sections.length === 2 ? 'lg:grid-cols-2' : 'lg:grid-cols-1';
  // 3-portal accounts need a wider canvas so the columns don't get squeezed.
  // 1- and 2-portal layouts are intentionally left unchanged.
  const maxW =
    sections.length >= 3 ? 'max-w-7xl' : sections.length === 1 ? 'max-w-md' : 'max-w-5xl';

  return (
    <div className="min-h-screen bg-gray-50 font-inter">
      {/* header: logo left · user info + logout right */}
      <header className="flex items-center justify-between px-6 py-5 sm:px-10">
        <div className="logo flex items-center gap-2">
          <img src={logoImg} alt="logo" className="w-auto h-8 md:h-10 object-contain" />
          <span className="text-xl sm:text-2xl font-bold text-[#404880]">Officer.app</span>
        </div>
        <div className="flex items-center gap-3">
          <div className="hidden sm:flex flex-col items-end">
            {userName && <p className="text-sm font-semibold text-gray-800 leading-tight">{userName}</p>}
            {userEmail && <p className="text-xs text-gray-400">{userEmail}</p>}
          </div>
          <div
            className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-xs font-bold text-white"
            style={{ background: ROLE.agency.gradient }}
          >
            {initial}
          </div>
          <LogoutButton onClick={handleLogout} />
        </div>
      </header>

      <div className={`mx-auto w-full ${maxW}`}>
        <div className="mb-6 px-6 sm:px-10">
          <p className="text-xs font-semibold uppercase tracking-widest text-gray-400">Your portals</p>
        </div>

        <div className={`grid gap-6 px-6 pb-16 sm:px-10 ${gridCols}`}>
          {sections}
        </div>
      </div>
    </div>
  );
};

export default MainView;
